import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMemo, useState } from 'react';
import { MetricKey, Report } from './types';
import { useListContext, useTranslate, useLocale } from 'ra-core';

const CHART_COLOR = '#3b82f6';

const emptyDataPoint = { impressions: 0, clicks: 0 };
const padDates = (
  minDate: number,
  maxDate: number,
  fromData: Array<Report>
): Array<Report> => {
  const d: Array<Report> = [];
  for (let i = minDate; i <= maxDate; i += 24 * 60 * 60 * 1000) {
    let item = fromData.find(r => r.date_timestamp === i);
    if (item) {
      d.push(item);
    } else {
      d.push({ date_timestamp: i, ...emptyDataPoint } as any);
    }
  }
  return d;
};

export const DailyTrendChart = () => {
  const { data = [], filterValues } = useListContext<Report>();
  const [metric, setMetric] = useState<MetricKey>('impressions');
  const t = useTranslate();
  const locale = useLocale();
  const { date_from, date_to } = filterValues;
  const XStart = date_from
    ? new Date(`${date_from}T00:00:00Z`).getTime()
    : null;
  const XEnd = date_to ? new Date(`${date_to}T00:00:00Z`).getTime() : null;
  const formatDate = (v: string) => {
    return new Date(v).toLocaleDateString(locale, { timeZone: 'UTC' });
  };

  const allData = useMemo(() => {
    // Add missing data points and
    // Add first and last dates from filter values
    const dMin = XStart || data[0]?.date_timestamp || 0;
    const dMax = XEnd || data[data.length - 1]?.date_timestamp || 0;
    const d = padDates(dMin, dMax, data);

    return d;
  }, [XEnd, XStart, data]);

  return (
    <div className="rounded-lg border border-border bg-card w-full">
      <div className="px-6 pb-4 flex flex-col items-start">
        <div className="mt-4 w-full">
          <div className="flex justify-between items-center mb-6">
            <h6 className="text-lg font-semibold text-foreground">
              {t('common.text.trend')}
            </h6>
            <Select
              value={metric}
              onValueChange={val => setMetric(val as MetricKey)}
            >
              <SelectTrigger className="w-[150px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="impressions">
                  {t('common.text.impressions')}
                </SelectItem>
                <SelectItem value="clicks">
                  {t('common.text.clicks')}
                </SelectItem>
                {/* <SelectItem value="actions">{t('common.text.actions')}</SelectItem> */}
              </SelectContent>
            </Select>
          </div>

          <ResponsiveContainer width={'100%'} height={400}>
            <AreaChart data={allData}>
              <XAxis
                dataKey="date_timestamp"
                scale="utc"
                type="number"
                domain={[XStart || 'dataMin', XEnd || 'dataMax + 1']}
                tickFormatter={formatDate}
              />
              <YAxis />
              <Tooltip
                wrapperStyle={{ color: '#000' }}
                labelFormatter={formatDate}
              />
              <Area
                connectNulls
                type="monotone"
                dataKey={metric}
                fill={CHART_COLOR}
                stroke={CHART_COLOR}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
